import {
  ActionIcon,
  Badge,
  Box,
  Card,
  Group,
  Loader,
  Paper,
  SegmentedControl,
  Text,
  Tooltip,
} from '@mantine/core';
import {
  IconDimensions,
  IconGridDots,
  IconRuler,
  IconZoomIn,
  IconZoomOut,
  IconZoomReset,
} from '@tabler/icons-react';
import React, { useEffect, useRef, useState } from 'react';

import { useVectorCadStore } from '../stores/vector-cad-store';
import type { MaterialType } from '../types';

export function ViewportCanvas() {
  const {
    geometryResult,
    weldScoreConfig,
    material,
    unit,
    showGrid,
    showDimensions,
    isCalculating,
    setMaterial,
    toggleGrid,
    toggleRulers,
    toggleDimensions,
    recalculate,
  } = useVectorCadStore();

  const containerRef = useRef<HTMLDivElement>(null);
  const [zoom, setZoom] = useState(1.8);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

  // Initial calculation on mount
  useEffect(() => {
    void recalculate();
  }, [recalculate]);

  // Center design on geometry change or reset
  const handleResetView = () => {
    setZoom(1.8);
    setPan({ x: 0, y: 0 });
  };

  const handleZoomIn = () => setZoom((z) => Math.min(6, z * 1.25));
  const handleZoomOut = () => setZoom((z) => Math.max(0.3, z / 1.25));

  // Mouse wheel zoom
  const handleWheel = (e: React.WheelEvent) => {
    e.preventDefault();
    const zoomFactor = e.deltaY < 0 ? 1.1 : 0.9;
    setZoom((z) => Math.min(6, Math.max(0.3, z * zoomFactor)));
  };

  // Mouse pan drag
  const handleMouseDown = (e: React.MouseEvent) => {
    if (e.button === 0 || e.button === 1) {
      setIsDragging(true);
      setDragStart({ x: e.clientX - pan.x, y: e.clientY - pan.y });
    }
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (isDragging) {
      setPan({ x: e.clientX - dragStart.x, y: e.clientY - dragStart.y });
    }
  };

  const handleMouseUp = () => setIsDragging(false);

  const bounds = geometryResult?.bounds || {
    minX: 0,
    minY: 0,
    maxX: 100,
    maxY: 40,
    widthMm: 100,
    heightMm: 40,
  };
  const widthDisplay =
    unit === 'inch'
      ? `${(bounds.widthMm / 25.4).toFixed(2)} in`
      : `${bounds.widthMm.toFixed(1)} mm`;
  const heightDisplay =
    unit === 'inch'
      ? `${(bounds.heightMm / 25.4).toFixed(2)} in`
      : `${bounds.heightMm.toFixed(1)} mm`;

  return (
    <Card
      withBorder
      p={0}
      radius='md'
      style={{
        height: '100%',
        minHeight: 520,
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        overflow: 'hidden',
        background: material === 'wireframe' ? '#141517' : '#1A1B1E',
        userSelect: 'none',
      }}
    >
      {/* Top Floating Viewport Toolbar */}
      <Box
        p='xs'
        style={{
          position: 'absolute',
          top: 12,
          left: 12,
          right: 12,
          zIndex: 10,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pointerEvents: 'none',
        }}
      >
        {/* Material Selector */}
        <Box style={{ pointerEvents: 'auto' }}>
          <SegmentedControl
            size='xs'
            value={material}
            onChange={(val) => setMaterial(val as MaterialType)}
            data={[
              { label: 'Wireframe', value: 'wireframe' },
              { label: 'Birch Wood', value: 'wood-birch' },
              { label: 'Walnut', value: 'wood-walnut' },
              { label: 'Acrylic Gold', value: 'acrylic-gold' },
              { label: 'Matte Black', value: 'acrylic-black' },
            ]}
          />
        </Box>

        {/* Viewport Action Icons */}
        <Paper
          withBorder
          radius='md'
          p={4}
          style={{
            pointerEvents: 'auto',
            background: 'rgba(26, 27, 30, 0.85)',
            backdropFilter: 'blur(8px)',
          }}
        >
          <Group gap={4}>
            <Tooltip label='Toggle Grid' position='bottom'>
              <ActionIcon
                variant={showGrid ? 'filled' : 'subtle'}
                color={showGrid ? 'blue' : 'gray'}
                size='sm'
                onClick={toggleGrid}
              >
                <IconGridDots size={16} />
              </ActionIcon>
            </Tooltip>
            <Tooltip label='Toggle Rulers' position='bottom'>
              <ActionIcon variant='subtle' color='gray' size='sm' onClick={toggleRulers}>
                <IconRuler size={16} />
              </ActionIcon>
            </Tooltip>
            <Tooltip label='Toggle Dimensions' position='bottom'>
              <ActionIcon
                variant={showDimensions ? 'filled' : 'subtle'}
                color={showDimensions ? 'blue' : 'gray'}
                size='sm'
                onClick={toggleDimensions}
              >
                <IconDimensions size={16} />
              </ActionIcon>
            </Tooltip>
            <Tooltip label='Zoom In' position='bottom'>
              <ActionIcon variant='subtle' color='gray' size='sm' onClick={handleZoomIn}>
                <IconZoomIn size={16} />
              </ActionIcon>
            </Tooltip>
            <Tooltip label='Zoom Out' position='bottom'>
              <ActionIcon variant='subtle' color='gray' size='sm' onClick={handleZoomOut}>
                <IconZoomOut size={16} />
              </ActionIcon>
            </Tooltip>
            <Tooltip label='Reset View' position='bottom'>
              <ActionIcon variant='subtle' color='gray' size='sm' onClick={handleResetView}>
                <IconZoomReset size={16} />
              </ActionIcon>
            </Tooltip>
          </Group>
        </Paper>
      </Box>

      {/* Main Interactive Canvas Area */}
      <Box
        ref={containerRef}
        style={{
          flex: 1,
          width: '100%',
          height: '100%',
          cursor: isDragging ? 'grabbing' : 'grab',
          position: 'relative',
          overflow: 'hidden',
        }}
        onWheel={handleWheel}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
      >
        {/* Calculating Overlay */}
        {isCalculating && (
          <Box
            style={{
              position: 'absolute',
              top: 16,
              right: 16,
              zIndex: 20,
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              background: 'rgba(0,0,0,0.6)',
              padding: '6px 12px',
              borderRadius: 6,
            }}
          >
            <Loader size='xs' color='blue' />
            <Text size='xs' c='dimmed'>
              Calculating Geometry...
            </Text>
          </Box>
        )}

        {/* SVG Viewport */}
        <svg
          style={{
            width: '100%',
            height: '100%',
            position: 'absolute',
            top: 0,
            left: 0,
          }}
        >
          <defs>
            {/* Grid Pattern */}
            <pattern
              id='grid-10mm'
              width={10 * zoom}
              height={10 * zoom}
              patternUnits='userSpaceOnUse'
            >
              <path
                d={`M ${10 * zoom} 0 L 0 0 0 ${10 * zoom}`}
                fill='none'
                stroke='rgba(255, 255, 255, 0.05)'
                strokeWidth='1'
              />
            </pattern>
            <pattern
              id='grid-50mm'
              width={50 * zoom}
              height={50 * zoom}
              patternUnits='userSpaceOnUse'
            >
              <rect width={50 * zoom} height={50 * zoom} fill='url(#grid-10mm)' />
              <path
                d={`M ${50 * zoom} 0 L 0 0 0 ${50 * zoom}`}
                fill='none'
                stroke='rgba(255, 255, 255, 0.12)'
                strokeWidth='1.2'
              />
            </pattern>

            {/* Realistic Material Gradients & Textures */}
            <linearGradient id='birch-wood' x1='0%' y1='0%' x2='100%' y2='100%'>
              <stop offset='0%' stopColor='#e8cfab' />
              <stop offset='30%' stopColor='#dfc39c' />
              <stop offset='70%' stopColor='#eed7b5' />
              <stop offset='100%' stopColor='#d8bc93' />
            </linearGradient>

            <linearGradient id='walnut-wood' x1='0%' y1='0%' x2='100%' y2='100%'>
              <stop offset='0%' stopColor='#4a3324' />
              <stop offset='50%' stopColor='#5b3e2b' />
              <stop offset='100%' stopColor='#3e291c' />
            </linearGradient>

            <linearGradient id='gold-acrylic' x1='0%' y1='0%' x2='100%' y2='100%'>
              <stop offset='0%' stopColor='#ffd700' />
              <stop offset='35%' stopColor='#fff2a3' />
              <stop offset='70%' stopColor='#e6b800' />
              <stop offset='100%' stopColor='#b38600' />
            </linearGradient>

            <filter id='drop-shadow' x='-10%' y='-10%' width='130%' height='130%'>
              <feDropShadow dx='0' dy='3' stdDeviation='4' floodOpacity='0.45' />
            </filter>
          </defs>

          {/* Background Grid */}
          {showGrid && <rect width='100%' height='100%' fill='url(#grid-50mm)' />}

          {/* Centered Interactive CAD Canvas Layer */}
          <g
            transform={`translate(${containerRef.current ? containerRef.current.clientWidth / 2 + pan.x : 300 + pan.x}, ${containerRef.current ? containerRef.current.clientHeight / 2 + pan.y : 200 + pan.y}) scale(${zoom}) translate(${-(bounds.minX + bounds.maxX) / 2}, ${-(bounds.minY + bounds.maxY) / 2})`}
          >
            {/* Realistic Material Render Mode */}
            {material !== 'wireframe' && (
              <g filter='url(#drop-shadow)'>
                {/* Backer Plate Material */}
                {geometryResult?.backerPaths.map((d, i) => (
                  <path
                    key={`mat-backer-${i}`}
                    d={d}
                    fill={
                      material === 'wood-walnut'
                        ? '#2e1e14'
                        : material === 'acrylic-black'
                          ? '#111'
                          : '#d2b48c'
                    }
                    stroke='#222'
                    strokeWidth={0.3}
                  />
                ))}

                {/* Scored Guide Lines on Backer */}
                {geometryResult?.guidePaths.map((d, i) => (
                  <path
                    key={`mat-guide-${i}`}
                    d={d}
                    fill='none'
                    stroke='rgba(0,0,0,0.35)'
                    strokeWidth={0.2}
                    strokeDasharray='1,1'
                  />
                ))}

                {/* Main Front Letters Material */}
                {geometryResult?.cutPaths.map((d, i) => (
                  <path
                    key={`mat-cut-${i}`}
                    d={d}
                    fill={
                      material === 'wood-birch'
                        ? 'url(#birch-wood)'
                        : material === 'wood-walnut'
                          ? 'url(#walnut-wood)'
                          : material === 'acrylic-gold'
                            ? 'url(#gold-acrylic)'
                            : material === 'acrylic-black'
                              ? '#222'
                              : '#ffffff'
                    }
                    stroke='rgba(0,0,0,0.4)'
                    strokeWidth={0.3}
                  />
                ))}

                {/* Laser Scored Inner Lines (Darker Etch) */}
                {geometryResult?.scorePaths.map((d, i) => (
                  <path
                    key={`mat-score-${i}`}
                    d={d}
                    fill='none'
                    stroke={
                      material === 'wood-walnut'
                        ? 'rgba(0,0,0,0.85)'
                        : material === 'wood-birch'
                          ? '#5c3a21'
                          : material === 'acrylic-gold'
                            ? '#8c6b00'
                            : 'rgba(0,0,0,0.7)'
                    }
                    strokeWidth={0.4}
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  />
                ))}
              </g>
            )}

            {/* Wireframe Laser Mode (Red = Cut, Blue = Score, Green = Backer) */}
            {material === 'wireframe' && (
              <g>
                {/* 1. Backer Cut Lines (Green) */}
                {geometryResult?.backerPaths.map((d, i) => (
                  <path
                    key={`wf-backer-${i}`}
                    d={d}
                    fill='rgba(0, 204, 0, 0.05)'
                    stroke='#00CC00'
                    strokeWidth={0.3}
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  />
                ))}

                {/* 2. Alignment Guides (Cyan) */}
                {geometryResult?.guidePaths.map((d, i) => (
                  <path
                    key={`wf-guide-${i}`}
                    d={d}
                    fill='none'
                    stroke='#00CCCC'
                    strokeWidth={0.2}
                    strokeDasharray='1,1'
                  />
                ))}

                {/* 3. Main Welded Cut Lines (Red) */}
                {geometryResult?.cutPaths.map((d, i) => (
                  <path
                    key={`wf-cut-${i}`}
                    d={d}
                    fill='rgba(255, 0, 0, 0.08)'
                    stroke={weldScoreConfig.cutColor || '#FF0000'}
                    strokeWidth={0.35}
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  />
                ))}

                {/* 4. Score Lines (Blue) */}
                {geometryResult?.scorePaths.map((d, i) => (
                  <path
                    key={`wf-score-${i}`}
                    d={d}
                    fill='none'
                    stroke={weldScoreConfig.scoreColor || '#0055FF'}
                    strokeWidth={weldScoreConfig.scoreStrokeWidthMm || 0.3}
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  />
                ))}
              </g>
            )}

            {/* Dimension Bounding Box & Guides */}
            {showDimensions && geometryResult && (
              <g pointerEvents='none'>
                <rect
                  x={bounds.minX}
                  y={bounds.minY}
                  width={bounds.widthMm}
                  height={bounds.heightMm}
                  fill='none'
                  stroke='rgba(255, 255, 255, 0.25)'
                  strokeWidth={0.2}
                  strokeDasharray='2,2'
                />
              </g>
            )}
          </g>
        </svg>

        {/* Bottom Floating Info Bar */}
        <Box
          p='xs'
          style={{
            position: 'absolute',
            bottom: 12,
            left: 12,
            right: 12,
            zIndex: 10,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            pointerEvents: 'none',
          }}
        >
          {/* Dimension Tag */}
          <Paper
            withBorder
            radius='md'
            px='sm'
            py={4}
            style={{
              pointerEvents: 'auto',
              background: 'rgba(26, 27, 30, 0.85)',
              backdropFilter: 'blur(8px)',
            }}
          >
            <Group gap='xs'>
              <Text size='xs' fw={600} c='white'>
                {widthDisplay} × {heightDisplay}
              </Text>
              <Badge size='xs' color='blue' variant='light'>
                Scale 1:1
              </Badge>
            </Group>
          </Paper>

          {/* Laser Stats */}
          {geometryResult && (
            <Paper
              withBorder
              radius='md'
              px='sm'
              py={4}
              style={{
                pointerEvents: 'auto',
                background: 'rgba(26, 27, 30, 0.85)',
                backdropFilter: 'blur(8px)',
              }}
            >
              <Group gap='md'>
                <Group gap={4}>
                  <Box
                    style={{ width: 8, height: 8, borderRadius: '50%', background: '#FF0000' }}
                  />
                  <Text size='xs' c='dimmed'>
                    Cut: <b style={{ color: '#fff' }}>{geometryResult.stats.cutLengthMm} mm</b>
                  </Text>
                </Group>
                <Group gap={4}>
                  <Box
                    style={{ width: 8, height: 8, borderRadius: '50%', background: '#0055FF' }}
                  />
                  <Text size='xs' c='dimmed'>
                    Score: <b style={{ color: '#fff' }}>{geometryResult.stats.scoreLengthMm} mm</b>
                  </Text>
                </Group>
                <Text size='xs' c='dimmed'>
                  Est. Laser:{' '}
                  <b style={{ color: '#51cf66' }}>{geometryResult.stats.estimatedLaserTimeSec}s</b>
                </Text>
              </Group>
            </Paper>
          )}
        </Box>
      </Box>
    </Card>
  );
}
